<svelte:options
	customElement={{
		tag: 'rf-split-flow',
		shadow: 'open',
	}}
/>

<script module>
	type ReframeSplitFlowProps = {
		id?: string;
		tag?: string;
		inverted?: boolean;
		'sticky-position'?: 'start' | 'end' | 'center';
		'parallax-interpolation'?: number;
		parallax?: boolean;
		debug?: boolean;
		responsive?: boolean;
		'no-collection'?: boolean;
	};
</script>

<script lang="ts">
	/**
	 * Import
	 */
	import { get } from 'svelte/store';
	import { createEventDispatcher, onMount } from 'svelte';
	import classNames from 'classnames';
	import { useScrollProgressOfElement } from '@src/composables/useScrollProgressOfElement.svelte';
	import parseBool from '@src/utils/bool';
	import { useSlottedElements } from '@src/composables/useSlottedElements';
	import debounced from '@src/utils/debounced';
	import { BREAKPOINTS } from '@src/utils/breakpoints';
	import { v4 as uuidv4 } from 'uuid';
	import { useCollection } from '@src/composables/useCollection.svelte.ts';

	let resizeObserver: ResizeObserver | null = null;
	const { element, forEachSlottedElement } = useSlottedElements();

	/**
	 * Props
	 */
	let {
		id = '',
		tag = 'section',
		inverted = false,
		'sticky-position': stickyVPosition = 'start',
		'parallax-interpolation': interpolation = 0.07,
		parallax = false,
		debug = false,
		responsive = true,
		'no-collection': noCollection = false,
	}: ReframeSplitFlowProps = $props();

	/**
	 * Constants
	 */
	const name = 'rf-split-flow';
	const componentId = uuidv4();

	/**
	 * Variables
	 */
	const isParallaxEnabled = parallax && interpolation > 0;
	const dispatch = createEventDispatcher();

	/**
	 * Composables
	 */

	export const collection = parseBool(noCollection)
		? undefined
		: useCollection(id, {
				element,
				moduleName: name,
				componentId,
			});

	/**
	 * Methods
	 */
	const onResize = () => {
		forEachSlottedElement((item) => {
			const currentContainer = get(element);
			if (currentContainer && currentContainer.clientWidth < BREAKPOINTS.xs && responsive) {
				if (item.tagName === 'RF-CANVAS') {
					item.setAttribute('fit', 'contain');
				}
			}
		}, 'sticky');
	};

	const onResizeDebounced = debounced(onResize, 100);

	/**
	 * Composables
	 */
	if (parallax) {
		useScrollProgressOfElement({
			el: element,
			interpolation,
			debug,
			onScroll: (e) => {
				dispatch('scroll', e);
			},
			onResize: (e) => {
				dispatch('resize', e);
				onResizeDebounced();
			},
		});
	}

	onMount(() => {
		const currentContainer = get(element);
		if (!element) return;
		if (!parallax && currentContainer) {
			resizeObserver = new ResizeObserver(onResizeDebounced);
			resizeObserver.observe(currentContainer);
		}
	});
</script>

<slot name="header"></slot>
<svelte:element
	this={tag}
	bind:this={$element}
	class={classNames(
		'split-flow',
		{ parallax: parseBool(parallax), responsive: parseBool(responsive), inverted: parseBool(inverted) },
		isParallaxEnabled ? 'center' : stickyVPosition
	)}
	part="rf-split-flow"
>
	<div class="split-flow__container">
		<div class="split-flow__sticky" part="split-flow__sticky">
			<slot name="sticky"></slot>
		</div>
		<div class="split-flow__scrollable" part="split-flow__scrollable"><slot name="scrollable"></slot></div>
	</div>
</svelte:element>
<slot name="footer"></slot>

<style lang="scss" global>
	@use 'assets/scss/core/rf-breakpoints' as breakpoint;

	.split-flow {
		--rf-split-flow-gap: var(--rf-col-gap, 20px);

		&__container {
			width: 100%;
			position: relative;
			display: flex;
			gap: var(--rf-split-flow-gap);
			justify-content: var(--rf-split-flow-justify-content, center);
			flex-direction: var(--rf-split-flow-direction, row);
			.inverted & {
				flex-direction: var(--rf-split-flow-direction, row-reverse);
			}
			.responsive & {
				@container rf-split-flow (width < #{breakpoint.$xs}) {
					flex-direction: var(--rf-split-flow-direction, column);
				}
			}
		}

		&__sticky {
			position: sticky;
			top: 0px;
			height: 100vh;
			max-height: 100vh;

			display: flex;
			flex-direction: column;
			width: calc(100% * var(--rf-split-flow-sticky-columns, 6) / var(--rf-columns) - var(--rf-split-flow-gap));
			.center & {
				justify-content: var(--rf-split-flow-sticky-vertical-position, center);
			}
			.start & {
				justify-content: var(--rf-split-flow-sticky-vertical-position, start);
			}
			.end & {
				justify-content: var(--rf-split-flow-sticky-vertical-position, end);
			}

			.responsive & {
				@container rf-split-flow (width < #{breakpoint.$s}) {
					width: calc(100% * var(--rf-split-flow-sticky-columns, 6) / var(--rf-columns) - var(--rf-split-flow-gap));
				}
				@container rf-split-flow (width < #{breakpoint.$xs}) {
					justify-content: var(--rf-split-flow-sticky-vertical-position, start);
					width: calc(100% * var(--rf-split-flow-sticky-columns, 12) / var(--rf-columns));
					max-height: 50vh;
					height: 50vh;
					display: block;
					z-index: 1;
					--rf-canvas-height: 50vh;
				}
			}

			::slotted(*) {
				max-height: 100vh;
				display: block;
				width: 100%;
				flex-grow: 0;
			}
			.parallax.center & {
				transform: translateY(
					calc(
						var(--rf-split-flow-parallax-treshold, 20%) * -1 + var(--rf-scroll-progress, 0) * var(--rf-split-flow-parallax-treshold, 20%) *
							2
					)
				);
			}
			.parallax.center.responsive & {
				@container rf-split-flow (width < #{breakpoint.$xs}) {
					transform: translateY(0);
				}
			}
		}

		&__scrollable {
			width: calc(100% * var(--rf-split-flow-scrollable-columns, 6) / var(--rf-columns) - var(--rf-split-flow-gap));
			.responsive & {
				@container rf-split-flow (width < #{breakpoint.$s}) {
					width: calc(100% * var(--rf-split-flow-scrollable-columns, 6) / var(--rf-columns) - var(--rf-split-flow-gap));
				}
				@container rf-split-flow (width < #{breakpoint.$xs}) {
					width: calc(100% * var(--rf-split-flow-scrollable-columns, 12) / var(--rf-columns));
				}
			}
		}
	}
</style>
